import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import PageHeading from "@/components/PageHeading/PageHeading";
import SectionHeading from "@/components/SectionHeading";
import RadioCard from "@/components/Radio/RadioCard";
import TextField from "@/components/TextField";
import DetailsCard from "@/components/DetailsCard";
import { SelectOptions } from "@/components/DetailsCard/DetailsCard";
import { MetricGridSkeleton, ObjectHeaderSkeleton, SectionHeadingSkeleton } from "@/components/Skeleton";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import usePageTitle from "@/hooks/usePageTitle";
import useProcessTracker from "@/hooks/useProcessTracker";
import { ProcessType } from "@/providers/ProcessTrackerProvider/processTypes";
import { Link, useNavigate } from "@/router";
import { formattedAddress } from "@/lib/addressHelpers";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import { CREATE_NOTE_MUTATION, type CreateNoteResponse } from "@/features/global/components/NotesTable/mutations";
import { NoteNotableValues, NotePriorityValues } from "@/types";
import type { MaterialNames } from "@/components/material_symbol_names";
import { FACILITY_ENUM_OPTIONS_QUERY } from "../queries";
import { FACILITY_MERGE_COUNT } from "./constants";
import {
  MERGE_PURGE_FACILITY_JOB_POSTS_QUERY,
  MERGE_PURGE_FACILITY_QUERY,
  type MergePurgeEmployment,
  type MergePurgeFacility,
  type MergePurgeFacilityJobPostsQueryResponse,
  type MergePurgeJobPost
} from "./queries";
import {
  FACILITIES_PROCESS_MERGE_MUTATION,
  FACILITIES_PROCESS_PURGE_MUTATION,
  type FacilitiesProcessMergeInput
} from "./mutations";

const emptyValue = <span className="opacity-20">--</span>;

const MERGE_PURGE_STEPS = [
  {
    id: "merge",
    label: "Merge request",
    processingLabel: "Sending merge request…",
    completedLabel: "Merge request sent",
    errorLabel: "Merge request failed"
  },
  {
    id: "purge",
    label: "Purge request",
    processingLabel: "Sending purge request…",
    completedLabel: "Purge request sent",
    errorLabel: "Purge request failed"
  }
];

type FacilityQueryResult = {
  data: MergePurgeFacility | null;
  loading: boolean;
  error: Error | undefined;
};

type JobPostsQueryResult = {
  data: MergePurgeFacilityJobPostsQueryResponse["jobPosts"];
  loading: boolean;
  error: Error | undefined;
};

type EnumLookup = {
  facilityTypeOptions?: { enumValues?: { name: string; description: string }[] };
  operatingStatusOptions?: { enumValues?: { name: string; description: string }[] };
};

function formatFacilityPhone(facility: MergePurgeFacility): string | null {
  if (!facility.phone?.phoneNumber && !facility.phone?.phoneFormatted) return null;

  if (facility.phone.phoneNumber) {
    let phoneToFormat = facility.phone.phoneNumber;
    if (facility.phone.phoneCountry && facility.phone.phoneCountry !== "US" && !phoneToFormat.startsWith("+")) {
      phoneToFormat = `+${phoneToFormat}`;
    }
    const phoneWithExtension = facility.phone.phoneExtension
      ? `${phoneToFormat} ext ${facility.phone.phoneExtension}`
      : phoneToFormat;
    return formatPhoneNumber(phoneWithExtension) || facility.phone.phoneFormatted || null;
  }

  return facility.phone.phoneFormatted || null;
}

function formatAddress(facility: MergePurgeFacility, type: "main" | "mailing"): string | null {
  const address = type === "main" ? facility.mainAddress : facility.mailingAddress;
  if (!address?.address1) return null;
  return formattedAddress(address);
}

function formatChapter(facility: MergePurgeFacility): string | null {
  if (!facility.chapter) return null;
  return facility.chapter.code ? `${facility.chapter.name} (${facility.chapter.code})` : facility.chapter.name;
}

function enumDescription(
  values: { name: string; description: string }[] | undefined,
  name: string | null | undefined
): string | null {
  if (!name) return null;
  return values?.find((value) => value.name === name)?.description ?? name;
}

function facilityRecordLabel(facility: MergePurgeFacility): string {
  return facility.legacyId ? `ID: ${facility.legacyId}` : `ID: ${facility.id}`;
}

function phoneMatchValue(facility: MergePurgeFacility): string | null {
  const number = facility.phone?.phoneNumber?.trim() || "";
  const extension = facility.phone?.phoneExtension?.trim() || "";
  if (!number && !extension) return null;
  return `${number}|${extension}`;
}

type SelectableFieldKey =
  | "facilityType"
  | "operatingStatus"
  | "phone"
  | "website"
  | "parentCompany"
  | "managementGroup"
  | "section"
  | "mainAddress"
  | "mailingAddress"
  | "chapter";

interface SelectableField {
  key: SelectableFieldKey;
  label: string;
  iconName: MaterialNames;
  getValue: (facility: MergePurgeFacility) => string | null;
  getDisplay?: (facility: MergePurgeFacility, enumData?: EnumLookup) => string | null;
}

const selectableFields: SelectableField[] = [
  {
    key: "facilityType",
    label: "Facility Type",
    iconName: "category",
    getValue: (facility) => facility.facilityType,
    getDisplay: (facility, enumData) =>
      enumDescription(enumData?.facilityTypeOptions?.enumValues, facility.facilityType)
  },
  {
    key: "operatingStatus",
    label: "Operating Status",
    iconName: "info",
    getValue: (facility) => facility.operatingStatus,
    getDisplay: (facility, enumData) =>
      enumDescription(enumData?.operatingStatusOptions?.enumValues, facility.operatingStatus)
  },
  {
    key: "phone",
    label: "Phone",
    iconName: "call",
    getValue: phoneMatchValue,
    getDisplay: formatFacilityPhone
  },
  {
    key: "website",
    label: "Website",
    iconName: "language",
    getValue: (facility) => facility.website
  },
  {
    key: "parentCompany",
    label: "Parent Company",
    iconName: "store",
    getValue: (facility) => facility.parentCompany?.id ?? null,
    getDisplay: (facility) => facility.parentCompany?.name ?? null
  },
  {
    key: "managementGroup",
    label: "Management Group",
    iconName: "apartment",
    getValue: (facility) => facility.managementGroup
  },
  { key: "section", label: "Section", iconName: "domain", getValue: (facility) => facility.section?.name ?? null },
  {
    key: "mainAddress",
    label: "Main Address",
    iconName: "location_on",
    getValue: (facility) => formatAddress(facility, "main")
  },
  {
    key: "mailingAddress",
    label: "Mailing Address",
    iconName: "markunread_mailbox",
    getValue: (facility) => formatAddress(facility, "mailing")
  },
  { key: "chapter", label: "Chapter", iconName: "group", getValue: formatChapter }
];

function fieldHeadline(
  field: SelectableField,
  facility: MergePurgeFacility,
  enumData?: EnumLookup
): string | JSX.Element {
  return field.getDisplay?.(facility, enumData) || field.getValue(facility) || emptyValue;
}

function defaultFacilityIdForField(
  field: SelectableField,
  facilities: MergePurgeFacility[],
  keeperId: string | null
): string | null {
  const keeper = facilities.find((facility) => facility.id === keeperId);
  if (keeper && field.getValue(keeper) != null) return keeper.id;
  const other = facilities.find((facility) => facility.id !== keeperId && field.getValue(facility) != null);
  return other?.id ?? keeperId;
}

function mergePhoneInput(
  facility: MergePurgeFacility | undefined
): Pick<FacilitiesProcessMergeInput, "phone" | "phoneExtension"> {
  const phoneNumber = facility?.phone?.phoneNumber;
  if (!phoneNumber) return {};
  if (facility.phone?.phoneExtension) {
    return { phone: phoneNumber, phoneExtension: facility.phone.phoneExtension };
  }
  return { phone: phoneNumber };
}

function previewOverflowOption(remaining: number, iconName: MaterialNames): SelectOptions {
  return {
    iconName,
    overline: "Additional records",
    headline: `and ${remaining} more`
  };
}

function employmentPreviewOptions(employments: MergePurgeEmployment[], totalCount: number): SelectOptions[] {
  const options: SelectOptions[] = employments.map((employment) => ({
    iconName: "badge",
    overline: employment.formattedTitle || employment.title || "Employment",
    headline: employment.person?.displayName || emptyValue
  }));
  const remaining = totalCount - employments.length;
  if (remaining > 0) options.push(previewOverflowOption(remaining, "badge"));
  return options;
}

function jobPostPreviewOptions(jobPosts: MergePurgeJobPost[], totalCount: number): SelectOptions[] {
  const options: SelectOptions[] = jobPosts.map((jobPost) => ({
    iconName: "work",
    overline: jobPost.stage || "Job post",
    headline: jobPost.name || emptyValue
  }));
  const remaining = totalCount - jobPosts.length;
  if (remaining > 0) options.push(previewOverflowOption(remaining, "work"));
  return options;
}

function PreviewEmpty({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center p-6 border border-sys-brand-outline rounded-lg opacity-50">
      {label}
    </div>
  );
}

function FacilityQueryLoader({
  facilityId,
  onResult
}: {
  facilityId: string;
  onResult: (facilityId: string, result: FacilityQueryResult) => void;
}) {
  const { data, loading, error } = useQuery(MERGE_PURGE_FACILITY_QUERY, { variables: { id: facilityId } });

  useEffect(() => {
    onResult(facilityId, { data: data?.facility ?? null, loading, error });
  }, [data, loading, error, facilityId, onResult]);

  return null;
}

function JobPostsQueryLoader({
  facilityId,
  onResult
}: {
  facilityId: string;
  onResult: (facilityId: string, result: JobPostsQueryResult) => void;
}) {
  const { data, loading, error } = useQuery(MERGE_PURGE_FACILITY_JOB_POSTS_QUERY, {
    variables: { facilityId }
  });

  useEffect(() => {
    onResult(facilityId, { data: data?.jobPosts ?? null, loading, error });
  }, [data, loading, error, facilityId, onResult]);

  return null;
}

export default function FacilityMergePurgePage() {
  useRequireCapability({ domain: DOMAIN.FACILITIES, permission: PERMISSION.manageFacilities });
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageMergePurge });
  usePageTitle("/facilities/facility-merge-purge", "Merge/Purge Facilities");

  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const [searchParams] = useSearchParams();
  const facilityIds = [...new Set(searchParams.get("ids")?.split(",").filter(Boolean) ?? [])];
  const navigate = useNavigate();
  const { startProcess, updateStep } = useProcessTracker();
  const [mergeFacility] = useMutation(FACILITIES_PROCESS_MERGE_MUTATION);
  const [purgeFacility] = useMutation(FACILITIES_PROCESS_PURGE_MUTATION);
  const [createNote] = useMutation<CreateNoteResponse>(CREATE_NOTE_MUTATION);

  const [results, setResults] = useState<Record<string, FacilityQueryResult>>({});
  const [jobPostResults, setJobPostResults] = useState<Record<string, JobPostsQueryResult>>({});
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(facilityIds[0] ?? null);
  const [selectedFacilityTypeFacilityId, setSelectedFacilityTypeFacilityId] = useState<string | null>(
    facilityIds[0] ?? null
  );
  const [selectedOperatingStatusFacilityId, setSelectedOperatingStatusFacilityId] = useState<string | null>(
    facilityIds[0] ?? null
  );
  const [selectedPhoneFacilityId, setSelectedPhoneFacilityId] = useState<string | null>(facilityIds[0] ?? null);
  const [selectedWebsiteFacilityId, setSelectedWebsiteFacilityId] = useState<string | null>(facilityIds[0] ?? null);
  const [selectedParentCompanyFacilityId, setSelectedParentCompanyFacilityId] = useState<string | null>(
    facilityIds[0] ?? null
  );
  const [selectedManagementGroupFacilityId, setSelectedManagementGroupFacilityId] = useState<string | null>(
    facilityIds[0] ?? null
  );
  const [selectedSectionFacilityId, setSelectedSectionFacilityId] = useState<string | null>(facilityIds[0] ?? null);
  const [selectedMainAddressFacilityId, setSelectedMainAddressFacilityId] = useState<string | null>(
    facilityIds[0] ?? null
  );
  const [selectedMailingAddressFacilityId, setSelectedMailingAddressFacilityId] = useState<string | null>(
    facilityIds[0] ?? null
  );
  const [selectedChapterFacilityId, setSelectedChapterFacilityId] = useState<string | null>(facilityIds[0] ?? null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const hasAppliedDefaultsRef = useRef(false);

  const canLoadComparison =
    flagsLoaded && flags["backoffice-facility-merge-purge"] === true && facilityIds.length === FACILITY_MERGE_COUNT;

  const { data: enumData, loading: enumLoading } = useQuery(FACILITY_ENUM_OPTIONS_QUERY, {
    skip: !canLoadComparison
  });

  const handleFacilityResult = useCallback((facilityId: string, result: FacilityQueryResult) => {
    setResults((prev) => {
      const existing = prev[facilityId];
      if (
        existing &&
        existing.loading === result.loading &&
        existing.data === result.data &&
        existing.error === result.error
      ) {
        return prev;
      }
      return { ...prev, [facilityId]: result };
    });
  }, []);

  const handleJobPostsResult = useCallback((facilityId: string, result: JobPostsQueryResult) => {
    setJobPostResults((prev) => {
      const existing = prev[facilityId];
      if (
        existing &&
        existing.loading === result.loading &&
        existing.data === result.data &&
        existing.error === result.error
      ) {
        return prev;
      }
      return { ...prev, [facilityId]: result };
    });
  }, []);

  const facilities = facilityIds
    .map((id) => results[id]?.data)
    .filter((facility): facility is MergePurgeFacility => facility != null);
  const loading =
    enumLoading ||
    facilityIds.some((id) => !results[id] || (results[id].loading && !results[id].error)) ||
    facilityIds.some((id) => !jobPostResults[id] || (jobPostResults[id].loading && !jobPostResults[id].error));
  const error = Object.values(results).find((result) => result.error)?.error;
  const selectedSectionId =
    facilities.find((facility) => facility.id === selectedSectionFacilityId)?.section?.id ?? null;

  useEffect(() => {
    if (!selectedFacilityId) return;
    setSelectedFacilityTypeFacilityId(selectedFacilityId);
    setSelectedOperatingStatusFacilityId(selectedFacilityId);
    setSelectedPhoneFacilityId(selectedFacilityId);
    setSelectedWebsiteFacilityId(selectedFacilityId);
    setSelectedParentCompanyFacilityId(selectedFacilityId);
    setSelectedManagementGroupFacilityId(selectedFacilityId);
    setSelectedSectionFacilityId(selectedFacilityId);
    setSelectedMainAddressFacilityId(selectedFacilityId);
    setSelectedMailingAddressFacilityId(selectedFacilityId);
    setSelectedChapterFacilityId(selectedFacilityId);
  }, [selectedFacilityId]);

  useEffect(() => {
    if (hasAppliedDefaultsRef.current) return;
    if (loading) return;
    if (facilities.length !== facilityIds.length) return;
    if (!selectedFacilityId) return;

    const setters: Record<SelectableFieldKey, (value: string | null) => void> = {
      facilityType: setSelectedFacilityTypeFacilityId,
      operatingStatus: setSelectedOperatingStatusFacilityId,
      phone: setSelectedPhoneFacilityId,
      website: setSelectedWebsiteFacilityId,
      parentCompany: setSelectedParentCompanyFacilityId,
      managementGroup: setSelectedManagementGroupFacilityId,
      section: setSelectedSectionFacilityId,
      mainAddress: setSelectedMainAddressFacilityId,
      mailingAddress: setSelectedMailingAddressFacilityId,
      chapter: setSelectedChapterFacilityId
    };
    selectableFields.forEach((field) => {
      setters[field.key](defaultFacilityIdForField(field, facilities, selectedFacilityId));
    });
    hasAppliedDefaultsRef.current = true;
  }, [loading, facilities, facilityIds.length, selectedFacilityId]);

  useEffect(() => {
    if (!selectedSectionId) return;
    const chapterFacility = facilities.find((facility) => facility.id === selectedChapterFacilityId);
    if (chapterFacility?.chapter && chapterFacility.section?.id !== selectedSectionId) {
      const matching = facilities.find((facility) => facility.section?.id === selectedSectionId);
      if (matching) setSelectedChapterFacilityId(matching.id);
    }
  }, [selectedSectionId, selectedChapterFacilityId, facilities]);

  const sourceFacility = facilities.find((facility) => facility.id !== selectedFacilityId);
  const targetFacility = facilities.find((facility) => facility.id === selectedFacilityId);
  const recordsReady = facilities.length === FACILITY_MERGE_COUNT;
  const canContinue = !loading && recordsReady;

  useEffect(() => {
    if (!sourceFacility || !targetFacility) return;
    const sourceLabel = sourceFacility.legacyId || sourceFacility.id;
    setInternalNote(`Facility record #${sourceLabel} was merged into this facility record and then purged`);
  }, [sourceFacility, targetFacility]);

  if (!flagsLoaded) {
    return null;
  }

  if (flags["backoffice-facility-merge-purge"] !== true) {
    return <Navigate to="/facilities" replace />;
  }

  if (facilityIds.length !== FACILITY_MERGE_COUNT) {
    return (
      <FocusedPageLayout backLink={{ to: "/facilities" }}>
        <EmptyState
          iconName="error"
          title="Invalid Selection"
          caption="Select exactly 2 facilities from the Facilities table to merge."
          action={{ children: "Back to Facilities", to: "/facilities" }}
        />
      </FocusedPageLayout>
    );
  }

  if (error || (!loading && !recordsReady)) {
    return (
      <FocusedPageLayout backLink={{ to: "/facilities" }}>
        <ErrorBoundary
          title="Failed to Load Facilities"
          message="An error occurred while loading facility records. Please try again."
          image="default"
          action={
            <Link to="/facilities">
              <Button variant="filled">Back to Facilities</Button>
            </Link>
          }
        />
      </FocusedPageLayout>
    );
  }

  const fieldSelections: Record<SelectableFieldKey, { value: string | null; setter: (value: string | null) => void }> =
    {
      facilityType: { value: selectedFacilityTypeFacilityId, setter: setSelectedFacilityTypeFacilityId },
      operatingStatus: { value: selectedOperatingStatusFacilityId, setter: setSelectedOperatingStatusFacilityId },
      phone: { value: selectedPhoneFacilityId, setter: setSelectedPhoneFacilityId },
      website: { value: selectedWebsiteFacilityId, setter: setSelectedWebsiteFacilityId },
      parentCompany: { value: selectedParentCompanyFacilityId, setter: setSelectedParentCompanyFacilityId },
      managementGroup: { value: selectedManagementGroupFacilityId, setter: setSelectedManagementGroupFacilityId },
      section: { value: selectedSectionFacilityId, setter: setSelectedSectionFacilityId },
      mainAddress: { value: selectedMainAddressFacilityId, setter: setSelectedMainAddressFacilityId },
      mailingAddress: { value: selectedMailingAddressFacilityId, setter: setSelectedMailingAddressFacilityId },
      chapter: { value: selectedChapterFacilityId, setter: setSelectedChapterFacilityId }
    };

  const matchingFields = selectableFields.filter((field) => {
    const values = facilities.map((facility) => field.getValue(facility));
    return values.every((value) => value === values[0]);
  });
  const differingFields = selectableFields.filter((field) => !matchingFields.includes(field));

  async function runMerge(processId: string): Promise<boolean> {
    if (!sourceFacility || !targetFacility) return false;
    const typeFacility = facilities.find((facility) => facility.id === selectedFacilityTypeFacilityId);
    const statusFacility = facilities.find((facility) => facility.id === selectedOperatingStatusFacilityId);
    const phoneFacility = facilities.find((facility) => facility.id === selectedPhoneFacilityId);
    const websiteFacility = facilities.find((facility) => facility.id === selectedWebsiteFacilityId);
    const parentFacility = facilities.find((facility) => facility.id === selectedParentCompanyFacilityId);
    const managementFacility = facilities.find((facility) => facility.id === selectedManagementGroupFacilityId);
    const sectionFacility = facilities.find((facility) => facility.id === selectedSectionFacilityId);
    const mainAddressFacility = facilities.find((facility) => facility.id === selectedMainAddressFacilityId);
    const mailingAddressFacility = facilities.find((facility) => facility.id === selectedMailingAddressFacilityId);
    const chapterFacility = facilities.find((facility) => facility.id === selectedChapterFacilityId);
    const chapterMatchesSection =
      !chapterFacility?.chapter || !selectedSectionId || chapterFacility.section?.id === selectedSectionId;

    updateStep(processId, "merge", "processing");
    try {
      const mergeResult = await mergeFacility({
        variables: {
          input: {
            sourceFacilityId: sourceFacility.id,
            targetFacilityId: targetFacility.id,
            facilityType: typeFacility?.facilityType ?? null,
            operatingStatus: statusFacility?.operatingStatus ?? null,
            ...mergePhoneInput(phoneFacility),
            website: websiteFacility?.website ?? null,
            parentCompanyId: parentFacility?.parentCompany?.id ?? null,
            managementGroup: managementFacility?.managementGroup ?? null,
            sectionId: sectionFacility?.section?.id ?? null,
            mainAddressId: mainAddressFacility?.mainAddress?.id ?? null,
            mailingAddressId: mailingAddressFacility?.mailingAddress?.id ?? null,
            chapterId: chapterMatchesSection ? (chapterFacility?.chapter?.id ?? null) : null
          }
        }
      });
      if (!mergeResult.data?.facilitiesProcessMerge?.success) {
        updateStep(
          processId,
          "merge",
          "error",
          mergeResult.data?.facilitiesProcessMerge?.message || "Merge request failed"
        );
        return false;
      }
      updateStep(processId, "merge", "complete");
      return true;
    } catch (mergeError) {
      updateStep(
        processId,
        "merge",
        "error",
        mergeError instanceof Error ? mergeError.message : "Merge request failed"
      );
      return false;
    }
  }

  async function runPurge(processId: string): Promise<boolean> {
    if (!sourceFacility) return false;
    updateStep(processId, "purge", "processing");
    try {
      const purgeResult = await purgeFacility({
        variables: { sourceFacilityId: sourceFacility.id }
      });
      if (!purgeResult.data?.facilitiesProcessPurge?.success) {
        updateStep(
          processId,
          "purge",
          "error",
          purgeResult.data?.facilitiesProcessPurge?.message || "Purge request failed"
        );
        return false;
      }
      updateStep(processId, "purge", "complete");
      return true;
    } catch (purgeError) {
      updateStep(
        processId,
        "purge",
        "error",
        purgeError instanceof Error ? purgeError.message : "Purge request failed"
      );
      return false;
    }
  }

  async function createKeeperNote(): Promise<void> {
    if (!targetFacility || !internalNote.trim()) return;
    await createNote({
      variables: {
        input: {
          notableId: targetFacility.id,
          notableType: NoteNotableValues.FACILITY,
          title: "Merge/Purge Process Run",
          content: internalNote.trim(),
          priority: NotePriorityValues.NONE
        }
      }
    });
  }

  return (
    <FocusedPageLayout
      backLink={{ to: "/facilities", title: "Cancel" }}
      stickyActions
      actions={
        <Button disabled={!canContinue} onClick={() => setConfirmOpen(true)}>
          Continue
        </Button>
      }
    >
      {facilityIds.map((id) => (
        <FacilityQueryLoader key={id} facilityId={id} onResult={handleFacilityResult} />
      ))}
      {facilityIds.map((id) => (
        <JobPostsQueryLoader key={`jobs-${id}`} facilityId={id} onResult={handleJobPostsResult} />
      ))}
      <div data-testid="facility-merge-purge-page" className="flex flex-col gap-6 pt-6 sm:pt-4">
        <PageHeading title="Merge/Purge Facilities" />

        {loading ? (
          <>
            <SectionHeadingSkeleton />
            <MetricGridSkeleton cards={facilityIds.length} hasHeading />
            <ObjectHeaderSkeleton />
          </>
        ) : (
          <div
            className="grid gap-x-4 overflow-x-auto"
            style={{ gridTemplateColumns: `repeat(${facilities.length}, minmax(320px, 1fr))` }}
          >
            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Facility Record to Keep"
                body="Select the facility record you want to keep. Related records move to this facility, then the other facility is purged."
              />
            </div>
            {facilities.map((facility) => (
              <RadioCard
                key={facility.id}
                name="facility-to-keep"
                testId={`facility-to-keep-${facility.id}`}
                headline={facility.name}
                supportingText={facilityRecordLabel(facility)}
                checked={selectedFacilityId === facility.id}
                onChange={() => setSelectedFacilityId(facility.id)}
                tagText={facility.archivedAt ? "Archived" : undefined}
                tagType="information"
              />
            ))}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Facility Details"
                body="Choose the values to keep when they differ. This includes type, operating status, phone, website, parent company, management group, section, addresses, and chapter."
              />
            </div>

            {matchingFields.length > 0 && (
              <>
                <div style={{ gridColumn: "1 / -1" }}>
                  <SectionHeading heading="Matching Fields" body="These values are the same on both facilities." />
                </div>
                {facilities.map((facility) => (
                  <DetailsCard
                    key={`match-${facility.id}`}
                    options={matchingFields.map((field) => ({
                      iconName: field.iconName,
                      overline: field.label,
                      headline: fieldHeadline(field, facility, enumData)
                    }))}
                  />
                ))}
              </>
            )}

            {differingFields.length > 0 && (
              <>
                <div style={{ gridColumn: "1 / -1" }}>
                  <SectionHeading
                    heading="Fields to Choose"
                    body="Select the value to keep in the final facility record."
                  />
                </div>
                {differingFields.map((field, fieldIdx) => (
                  <div
                    key={field.key}
                    className={fieldIdx > 0 ? "mt-3" : ""}
                    style={{
                      gridColumn: "1 / -1",
                      display: "grid",
                      gridTemplateColumns: `repeat(${facilities.length}, minmax(320px, 1fr))`,
                      gap: "1rem"
                    }}
                  >
                    {facilities.map((facility) => {
                      const chapterDisabled =
                        field.key === "chapter" &&
                        !!facility.chapter &&
                        !!selectedSectionId &&
                        facility.section?.id !== selectedSectionId;
                      return (
                        <RadioCard
                          key={facility.id}
                          name={`${field.key}-to-keep`}
                          testId={`${field.key}-to-keep-${facility.id}`}
                          headline={fieldHeadline(field, facility, enumData)}
                          supportingText={field.label}
                          checked={fieldSelections[field.key]?.value === facility.id}
                          onChange={() => fieldSelections[field.key]?.setter(facility.id)}
                          disabled={chapterDisabled}
                        />
                      );
                    })}
                  </div>
                ))}
              </>
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Active Employments"
                body="Active employments are shown for reference. All employments, including ended ones, move to the kept facility."
              />
            </div>
            {facilities.map((facility) => {
              const nodes = facility.employments?.nodes ?? [];
              const totalCount = facility.employments?.totalCount ?? 0;
              const options = employmentPreviewOptions(nodes, totalCount);
              return options.length > 0 ? (
                <DetailsCard key={`employments-${facility.id}`} options={options} />
              ) : (
                <PreviewEmpty key={`employments-${facility.id}`} label="No active employments" />
              );
            })}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Open Job Posts"
                body="Open job posts are shown for reference. Closed job posts still move to the kept facility."
              />
            </div>
            {facilities.map((facility) => {
              const jobPostsResult = jobPostResults[facility.id];
              if (jobPostsResult?.error) {
                return (
                  <ErrorBoundary
                    key={`jobs-${facility.id}`}
                    variant="inline"
                    title="Failed to load job posts"
                    message={jobPostsResult.error.message}
                  />
                );
              }
              const nodes = jobPostsResult?.data?.nodes ?? [];
              const totalCount = jobPostsResult?.data?.totalCount ?? 0;
              const options = jobPostPreviewOptions(nodes, totalCount);
              return options.length > 0 ? (
                <DetailsCard key={`jobs-${facility.id}`} options={options} />
              ) : (
                <PreviewEmpty key={`jobs-${facility.id}`} label="No open job posts" />
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        headline="Start Merge/Purge Process"
        open={confirmOpen}
        onClosed={() => setConfirmOpen(false)}
        hasFormElements
        actions={
          <>
            <Button variant="text" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="filled"
              disabled={!sourceFacility || !targetFacility}
              onClick={async () => {
                if (!sourceFacility || !targetFacility) return;
                const processId = startProcess(ProcessType.MERGE_PURGE, "Merge/Purge Facilities", MERGE_PURGE_STEPS, {
                  completionAction: {
                    label: "View Facility",
                    to: `/facilities/${targetFacility.id}/details/contact`
                  },
                  errorAction: { label: "Back to Facilities", to: "/facilities" }
                });
                setConfirmOpen(false);
                navigate("/facilities");
                if (!(await runMerge(processId))) return;
                if (!(await runPurge(processId))) return;
                await createKeeperNote();
              }}
            >
              Start Process
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-6 text-body-medium">
          <p>The following requests will be sent:</p>
          <ol className="list-decimal pl-5 flex flex-col gap-1">
            {sourceFacility && targetFacility ? (
              <>
                <li>
                  Merge {sourceFacility.name} into {targetFacility.name}
                </li>
                <li>Purge {sourceFacility.name}</li>
              </>
            ) : (
              <>
                <li>Merge the selected facility records</li>
                <li>Purge the duplicate facility record</li>
              </>
            )}
          </ol>
          <p>
            A progress tracker will show whether each request succeeded. You can continue using the system while this
            runs.
          </p>
          <TextField
            label="Internal Note"
            value={internalNote}
            onChangeText={setInternalNote}
            rows={3}
            type="textarea"
          />
        </div>
      </Dialog>
    </FocusedPageLayout>
  );
}
